import React, { useCallback, useEffect, useState } from 'react';

import { Button, CircularProgress, Dialog, DialogActions, SelectChangeEvent, Typography, useTheme } from '@mui/material';

import { AccordionSection } from '~/components/AccordionSection';
import { useUpdateVehicleOptions, useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { IConfigurationSuggestion } from '~/domain/IConfigurationSuggestion';
import { IConfigurationTrim } from '~/domain/IConfigurationTrim';
import { ConfigurationSuggestionDialog } from '~/pages/RequestPage/ConfigurationSuggestionDialog';

import { ConfigurationOptionColors } from './ConfigurationOptionColors';
import { ConfigurationPackSetTrims } from './ConfigurationPackSetTrims';

interface IConfigurationOptionPropsTrims {
  name: string | undefined;
  code: string | undefined;
  client: string | undefined;
  trims: IConfigurationTrim[];
}

interface IConfigurationSuggestions {
  suggestChanges: IConfigurationSuggestion[];
  featuresOnAccept: string[];
}

export const ConfigurationOptionTrims: React.FC<IConfigurationOptionPropsTrims> = (props) => {

  const { name, client } = props;
  let { trims } = props;
  if (trims === undefined) {
    trims = [];
  }

  const { palette } = useTheme();

  const vehicleOptions = useVehicleOptions();
  const updateVehicleOptions = useUpdateVehicleOptions();
  const { features } = vehicleOptions;
  const [{ suggestChanges = [], featuresOnAccept = [] } = {}, setConfigurationSuggestions] = useState<IConfigurationSuggestions | undefined>();
  const [suggestionWorker, setSuggestionWorker] = useState<Worker>();

  const [isComputingSuggestion, setIsComputingSuggestion] = useState<boolean>(false);
  const [currentSelected, setCurrentSelected] = useState<string>(trims[0].code);
  const [currentSelectedObject, setCurrentSelectedObject] = useState<IConfigurationTrim>(trims[0]);

  const selection = trims.find(x => currentSelected.includes(x.code));

  const onCancelSuggestionWorker = useCallback(() => {
    suggestionWorker?.terminate();
    setIsComputingSuggestion(false);
    setSuggestionWorker(undefined);
  }, [suggestionWorker]);

  const addToFeaturesArray = useCallback((currentSelected: string, features: string[]) => {
    const index = features.indexOf(currentSelected);
    if (index === -1) {
      const newFeatures = [...features];

      newFeatures.push(currentSelected);
      updateVehicleOptions({
        ...vehicleOptions,
        features: newFeatures,
      });
    }
  }, [updateVehicleOptions, vehicleOptions]);

  useEffect(() => {
    addToFeaturesArray(currentSelected, features);
  }, [addToFeaturesArray, currentSelected, features]);

  const handleTrimsChange = useCallback((e: SelectChangeEvent) => {
    const code = e.target.value;

    const index = features.indexOf(selection?.code || '');

    const newPack = trims.find(x => x.code === code);
    const oldPack = trims.find(x => x.code === features[index]);

    //check if exists in currenbt features
    setCurrentSelected(code);

    //set interior picks into state
    if (newPack) {
      setCurrentSelectedObject(newPack);
    }

    let newFeatures = [...features];

    if (index > -1 && features[index] !== code && newPack && oldPack) {

      newFeatures = [];
      newFeatures.push(code);
      newFeatures.push(newPack.colors[0].code);
      newFeatures.push(newPack.colors[0].packs[0].code);

      newFeatures[index] = code;
      updateVehicleOptions({
        ...vehicleOptions,
        features: newFeatures,
      });
    }
    else {
      newFeatures.push(code);
      updateVehicleOptions({
        ...vehicleOptions,
        features: newFeatures,
      });
    }

  }, [features, trims, selection, updateVehicleOptions, vehicleOptions]);

  return (
    <>
      {/* suppress dialog code for now */}
      <Dialog
        open={isComputingSuggestion}
        PaperProps={{
          sx: {
            width: 570,
            maxHeight: 544,
            p: '34px 40px 40px 40px',
            alignItems: 'center',
          },
        }}
      >
        <Typography>Trying to find a suitable suggestion...</Typography>
        <CircularProgress />
        <DialogActions>
          <Button autoFocus color="dark" size="small" onClick={onCancelSuggestionWorker}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* create accordian section for each trim from parent map */}
      <AccordionSection
        contenttype={'trims'}
        defaultExpanded={true}
        label={
          <>
            <span style={{ color: palette.primary.dark, marginRight: 10 }}>
              – Trims
            </span>
          </>
        }
      >
        <ConfigurationPackSetTrims
          label={name}
          onChange={handleTrimsChange}
          packs={trims}
          value={currentSelected ?? trims[0].code}
        />

        <>
          <ConfigurationOptionColors
            name={currentSelectedObject.name}
            code={currentSelectedObject.code}
            client={client}
            trim={currentSelected ?? trims[0].code}
            colors={currentSelectedObject.colors}
          />
        </>

        {suggestChanges.length > 0 && (
          <ConfigurationSuggestionDialog
            suggestChanges={suggestChanges}
            onCancel={() => {
              setConfigurationSuggestions(undefined);
            }}
            onAccept={() => {
              updateVehicleOptions({
                ...vehicleOptions,
                features: featuresOnAccept,
              });
              setConfigurationSuggestions(undefined);
            }}
          />
        )}
      </AccordionSection>

    </>
  );
};
